import {inject, Injectable, NgZone} from '@angular/core';
import {DOCUMENT} from '@angular/common';
import {EnvironmentService} from '../../services/environment.service';

export interface ConversationPreviewOptions {
    conversationId: string;
    clientId: string;
    chatbotId: string;
    title?: string;
    widgetScriptUrl?: string;
    runtimeConfigOverride?: Record<string, string>;
}

const PREVIEW_OPEN_EVENT = 'shine:preview-open';

@Injectable({providedIn: 'root'})
export class ConversationPreviewService {
    private readonly environmentService = inject(EnvironmentService);
    private readonly document = inject<Document>(DOCUMENT);
    private readonly ngZone = inject(NgZone);

    private scriptLoader: Promise<void> | null = null;
    private scriptLoaded = false;

    open(options: ConversationPreviewOptions): void {
        const widgetScriptUrl = options.widgetScriptUrl || this.environmentService.widgetScriptUrl;
        const win = this.document?.defaultView;

        if (!win) {
            return;
        }

        if (!widgetScriptUrl) {
            console.warn('Widget script URL is not configured; unable to preview conversation.');
            return;
        }

        this.applyConversationContext(win, options);

        this.ensureWidgetScript(widgetScriptUrl)
            .then(() => {
                this.dispatchPreviewOpen(win);
                this.openModalWhenReady();
            })
            .catch((error) => console.error('Failed to load widget script for preview', error));

        // Best effort: if widget already exists, attempt to open immediately
        this.openModalWhenReady();
    }

    private applyConversationContext(win: Window, options: ConversationPreviewOptions): void {
        const settingsKey = `${options.clientId}-${options.chatbotId}-conversationId`;
        const shineSettings = (win as any).shineSettings ?? {};
        shineSettings.client = options.clientId;
        shineSettings.chatbot = options.chatbotId;
        shineSettings.inlineOnly = false;
        shineSettings.debug = true;
        shineSettings.forceConversationId = options.conversationId;
        shineSettings[settingsKey] = options.conversationId;
        (win as any).shineSettings = shineSettings;

        if (options.runtimeConfigOverride) {
            const runtimeConfig = (win as any).RUNTIME_CONFIG ?? {};
            (win as any).RUNTIME_CONFIG = {
                ...runtimeConfig,
                ...options.runtimeConfigOverride
            };
        }

        const expires = new Date(Date.now() + 60 * 60 * 1000).toUTCString();
        this.document.cookie = `${encodeURIComponent(settingsKey)}=${encodeURIComponent(options.conversationId)}; path=/; expires=${expires}`;
        this.dispatchConversationOverride(win, options.conversationId);
    }

    private ensureWidgetScript(src: string): Promise<void> {
        if (this.scriptLoaded || typeof window === 'undefined') {
            return Promise.resolve();
        }

        if (this.scriptLoader) {
            return this.scriptLoader;
        }

        this.scriptLoader = new Promise<void>((resolve, reject) => {
            const existing = this.document.getElementById('shine-widget-loader-script') as HTMLScriptElement | null;
            if (existing) {
                if (existing.dataset['loaded'] === 'true') {
                    this.scriptLoaded = true;
                    resolve();
                    return;
                }

                existing.addEventListener('load', () => {
                    this.scriptLoaded = true;
                    existing.dataset['loaded'] = 'true';
                    resolve();
                }, {once: true});
                existing.addEventListener('error', reject, {once: true});
                return;
            }

            const script = this.document.createElement('script');
            script.id = 'shine-widget-loader-script';
            script.type = 'text/javascript';
            script.src = src;
            script.async = true;
            script.onload = () => {
                this.scriptLoaded = true;
                script.dataset['loaded'] = 'true';
                resolve();
            };
            script.onerror = (err) => reject(err);
            this.document.head.appendChild(script);
        });

        return this.scriptLoader;
    }

    private openModalWhenReady(attempt = 0): void {
        this.ngZone.runOutsideAngular(() => {
            const widget = this.document.querySelector('shine-widget-component') as (HTMLElement & {
                getRenderRoot?: () => ParentNode;
            }) | null;
            const root = widget?.getRenderRoot?.() ?? widget?.shadowRoot;
            const modal = (root?.querySelector('#shineModal') as HTMLElement | null)
                ?? this.document.getElementById('shineModal');
            if (modal) {
                modal.style.display = 'flex';
                modal.style.minHeight = '100vh';
                return;
            }

            if (attempt > 40) {
                console.warn('Chatbot widget did not initialize; check client/chatbot configuration.');
                return;
            }

            setTimeout(() => this.openModalWhenReady(attempt + 1), 200);
        });
    }

    private dispatchConversationOverride(win: Window, conversationId: string): void {
        try {
            const event = new CustomEvent('shine:preview-set-conversation', {detail: {conversationId}});
            win.dispatchEvent(event);
        } catch {
            /* no-op */
        }
    }

    private dispatchPreviewOpen(win: Window): void {
        try {
            win.dispatchEvent(new CustomEvent(PREVIEW_OPEN_EVENT));
        } catch {
            /* legacy modal polling remains available */
        }
    }
}
